import { useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { MembershipBillingCycle } from '@goelectric/shared-types';
import { BILLING_CYCLE_LABELS, BILLING_CYCLE_VALUES } from '../utils/format';
import {
  emptyMembershipFormValues,
  MEMBERSHIP_BENEFIT_MAX,
  MEMBERSHIP_BENEFITS_MAX,
  MEMBERSHIP_DEFAULT_CURRENCY,
  MEMBERSHIP_DESCRIPTION_MAX,
  MEMBERSHIP_DISCOUNT_MAX,
  MEMBERSHIP_DISCOUNT_MIN,
  MEMBERSHIP_NAME_MAX,
  MEMBERSHIP_PRICE_MAX,
  normalizeBenefits,
  toAntdFieldPath,
  validateMembershipFormValues,
  type MembershipFormValues,
} from '../utils/membershipForm';

const CYCLE_OPTIONS = BILLING_CYCLE_VALUES.map((value) => ({
  value,
  label: BILLING_CYCLE_LABELS[value],
}));

export interface MembershipFormProps {
  /** Valores iniciales (edición); por defecto, alta mensual y activa. */
  initialValues?: MembershipFormValues;
  mode?: 'create' | 'edit';
  submitting?: boolean;
  onSubmit: (values: MembershipFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Formulario de alta/edición de Membresías (US-2.4).
 *
 * - Nombre, ciclo y precio obligatorios; descripción opcional.
 * - Beneficios como lista dinámica de strings (las filas vacías se descartan).
 * - Descuento de tarifa 0–100 y switch de activa.
 * - Moneda fija COP (solo lectura) y `id` nunca visible ni editable.
 * - Validación inline con mensajes en español y red de seguridad antes de enviar.
 */
export function MembershipForm({
  initialValues,
  mode = 'create',
  submitting = false,
  onSubmit,
  onCancel,
}: MembershipFormProps) {
  const [form] = Form.useForm<MembershipFormValues>();
  const defaults = initialValues ?? emptyMembershipFormValues();
  const isEdit = mode === 'edit';
  const [validationSummary, setValidationSummary] = useState<string[]>([]);

  /**
   * Red de seguridad final: las reglas inline dan feedback inmediato, pero acá se
   * vuelve a validar el modelo completo antes de enviar. Si hay problemas, se
   * mapean a paths de antd y no se llama a `onSubmit`.
   */
  const handleFinish = (values: MembershipFormValues) => {
    const normalized: MembershipFormValues = {
      ...values,
      name: values.name?.trim() ?? '',
      benefits: normalizeBenefits(values.benefits),
      tariffDiscountPct: values.tariffDiscountPct ?? 0,
    };
    const issues = validateMembershipFormValues(normalized);
    if (issues.length > 0) {
      setValidationSummary(issues.map((issue) => issue.message));
      form.setFields(
        issues.map((issue) => ({
          name: toAntdFieldPath(issue.field),
          errors: [issue.message],
        })) as Parameters<typeof form.setFields>[0],
      );
      return;
    }

    setValidationSummary([]);
    void onSubmit({ ...normalized, active: values.active ?? true });
  };

  return (
    <Form<MembershipFormValues>
      form={form}
      layout="vertical"
      initialValues={defaults}
      onFinish={handleFinish}
      onValuesChange={() => {
        if (validationSummary.length > 0) setValidationSummary([]);
      }}
      requiredMark={false}
      disabled={submitting}
    >
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Datos de la membresía
      </Typography.Title>
      {validationSummary.length > 0 && (
        <Alert
          type="error"
          showIcon
          role="alert"
          aria-live="assertive"
          message="Revisa los datos de la membresía"
          description={
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {validationSummary.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        label="Nombre"
        name="name"
        rules={[
          {
            required: true,
            whitespace: true,
            message: 'Escribe el nombre de la membresía.',
          },
          {
            max: MEMBERSHIP_NAME_MAX,
            message: `El nombre no puede superar ${MEMBERSHIP_NAME_MAX} caracteres.`,
          },
        ]}
      >
        <Input
          placeholder="Ej. Membresía Premium"
          autoComplete="off"
          maxLength={MEMBERSHIP_NAME_MAX}
          showCount
        />
      </Form.Item>

      <Form.Item
        label="Descripción (opcional)"
        name="description"
        rules={[
          {
            max: MEMBERSHIP_DESCRIPTION_MAX,
            message: `La descripción no puede superar ${MEMBERSHIP_DESCRIPTION_MAX} caracteres.`,
          },
        ]}
      >
        <Input.TextArea
          rows={2}
          maxLength={MEMBERSHIP_DESCRIPTION_MAX}
          showCount
          placeholder="Ej. Tarifa preferencial y sin cargo de sesión."
        />
      </Form.Item>

      <Space size={16} wrap align="start">
        <Form.Item
          label="Ciclo de facturación"
          name="billingCycle"
          style={{ minWidth: 220 }}
          rules={[{ required: true, message: 'Elige el ciclo de facturación.' }]}
        >
          <Select<MembershipBillingCycle>
            options={CYCLE_OPTIONS}
            aria-label="Ciclo de facturación"
            style={{ width: 220 }}
          />
        </Form.Item>

        <Form.Item
          label={`Precio (${MEMBERSHIP_DEFAULT_CURRENCY})`}
          name="priceCop"
          style={{ minWidth: 220 }}
          rules={[
            { required: true, message: 'Ingresa el precio de la membresía.' },
            {
              type: 'number',
              min: 0,
              max: MEMBERSHIP_PRICE_MAX,
              message: 'El precio debe ser mayor o igual a 0.',
            },
          ]}
        >
          <InputNumber
            min={0}
            max={MEMBERSHIP_PRICE_MAX}
            step={1000}
            precision={2}
            aria-label="Precio de la membresía"
            style={{ width: 240 }}
            placeholder="49900"
          />
        </Form.Item>

        <Form.Item label="Moneda" htmlFor="membership-currency">
          <Input
            id="membership-currency"
            value={MEMBERSHIP_DEFAULT_CURRENCY}
            readOnly
            aria-label="Moneda"
            style={{ width: 100 }}
          />
        </Form.Item>

        <Form.Item
          label="Descuento de tarifa (%)"
          name="tariffDiscountPct"
          style={{ minWidth: 220 }}
          rules={[
            {
              type: 'number',
              min: MEMBERSHIP_DISCOUNT_MIN,
              max: MEMBERSHIP_DISCOUNT_MAX,
              message: 'El descuento debe estar entre 0 y 100.',
            },
          ]}
        >
          <InputNumber
            min={MEMBERSHIP_DISCOUNT_MIN}
            max={MEMBERSHIP_DISCOUNT_MAX}
            step={1}
            precision={2}
            aria-label="Descuento de tarifa (%)"
            style={{ width: 200 }}
            placeholder="15"
          />
        </Form.Item>
      </Space>

      <Form.Item
        label="Activa"
        name="active"
        valuePropName="checked"
        extra="Una membresía inactiva conserva su historial, pero no se ofrece en la PWA."
      >
        <Switch
          checkedChildren="Activa"
          unCheckedChildren="Inactiva"
          aria-label="Activa"
        />
      </Form.Item>

      <Divider />

      <Typography.Title level={5}>Beneficios</Typography.Title>
      <Typography.Paragraph type="secondary">
        Agrega los beneficios uno por uno. Puedes quitarlos o reordenarlos editando el
        texto antes de guardar.
      </Typography.Paragraph>
      <Form.List
        name="benefits"
        rules={[
          {
            validator: async (_, value: string[] | undefined) => {
              if ((value ?? []).length > MEMBERSHIP_BENEFITS_MAX) {
                throw new Error(
                  `No puedes agregar más de ${MEMBERSHIP_BENEFITS_MAX} beneficios.`,
                );
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {fields.map((field, index) => {
              const { key, ...restField } = field;
              return (
                <Space key={key} align="start" style={{ display: 'flex' }}>
                  <Form.Item
                    {...restField}
                    style={{ flex: 1, marginBottom: 8, minWidth: 320 }}
                    rules={[
                      {
                        max: MEMBERSHIP_BENEFIT_MAX,
                        message: `Cada beneficio no puede superar ${MEMBERSHIP_BENEFIT_MAX} caracteres.`,
                      },
                    ]}
                  >
                    <Input
                      aria-label={`Beneficio ${index + 1}`}
                      placeholder="Ej. Tarifa preferencial"
                      maxLength={MEMBERSHIP_BENEFIT_MAX}
                    />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    aria-label={`Eliminar beneficio ${index + 1}`}
                    onClick={() => remove(field.name)}
                  />
                </Space>
              );
            })}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              aria-label="Agregar beneficio"
              onClick={() => add('')}
              disabled={fields.length >= MEMBERSHIP_BENEFITS_MAX}
              style={{ width: 220 }}
            >
              Agregar beneficio
            </Button>
          </Space>
        )}
      </Form.List>

      <Divider />

      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {isEdit ? 'Guardar cambios' : 'Crear membresía'}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
      </Space>
    </Form>
  );
}
